'use client'

import { useState } from 'react'

interface SectorData {
  category: string
  up: number
  down: number
  total: number
  avgChange: number
}

interface Props {
  sectors: SectorData[]
}

export default function SectorSentiment({ sectors }: Props) {
  const [expanded, setExpanded] = useState(false)

  const greenSectors = sectors.filter((s) => s.avgChange > 0)
  const redSectors = sectors.filter((s) => s.avgChange < 0)
  const bestSector = sectors[0]
  const worstSector = sectors[sectors.length - 1]
  const spread = bestSector ? Math.abs(bestSector.avgChange - worstSector.avgChange) : 0
  const maxAbs = Math.max(...sectors.map((s) => Math.abs(s.avgChange)), 0.5)

  // Auto-generated market signal
  let marketSignal = 'Consolidation'
  let signalColor = 'bg-zinc-500'
  if (greenSectors.length >= 5) {
    marketSignal = 'Broad-based rally'
    signalColor = 'bg-green-500'
  } else if (redSectors.length >= 5) {
    marketSignal = 'Market-wide selloff'
    signalColor = 'bg-red-500'
  } else if (spread > 3) {
    marketSignal = greenSectors.length >= redSectors.length ? 'Selective risk-on' : 'Selective risk-off'
    signalColor = greenSectors.length >= redSectors.length ? 'bg-green-500' : 'bg-red-500'
  } else if (spread < 1) {
    marketSignal = 'Consolidation'
    signalColor = 'bg-zinc-500'
  } else {
    marketSignal = greenSectors.length > redSectors.length ? 'Cautiously bullish' : 'Cautiously bearish'
    signalColor = greenSectors.length > redSectors.length ? 'bg-green-500' : 'bg-red-500'
  }

  // Commentary
  const signalDesc = greenSectors.length >= 5
    ? `${greenSectors.length} of ${sectors.length} sectors are green with ${bestSector.category} leading at +${bestSector.avgChange.toFixed(2)}%. Broad bullish sentiment across the market.`
    : redSectors.length >= 5
    ? `${redSectors.length} of ${sectors.length} sectors are red with ${worstSector.category} down ${worstSector.avgChange.toFixed(2)}%. Selling pressure across the board.`
    : `${greenSectors.length} of ${sectors.length} sectors are green with ${bestSector.category} leading at +${bestSector.avgChange.toFixed(2)}%. Capital is rotating into yield-generating sectors while speculative ones sell off.`

  const strongestDesc = bestSector.up / bestSector.total > 0.7
    ? 'Broad-based strength suggests institutional flow rather than a single coin pump.'
    : 'Mixed breadth — a few large caps may be carrying the sector.'

  const weakestDesc = worstSector.down / worstSector.total > 0.7
    ? 'Broad selloff across the sector signals risk-off sentiment for speculative assets.'
    : 'Narrow weakness — not all coins are affected equally.'

  const rotationDesc = greenSectors.length > 0 && redSectors.length > 0
    ? `Capital moving from ${redSectors.map(s => s.category).join(' and ')} into ${greenSectors.map(s => s.category).join(' and ')}. ${spread > 5 ? 'Strong divergence — a clear rotation pattern.' : 'Moderate rotation — watch for follow-through.'}`
    : 'No rotation — all sectors moving in the same direction.'

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
      {/* LEFT: Diverging bar chart */}
      <div className="border border-zinc-800 px-4 py-4 self-start">
        <div className="flex flex-col">
          {sectors.map((sector, idx) => {
            const barPct = (Math.abs(sector.avgChange) / maxAbs) * 45

            return (
              <div
                key={sector.category}
                className={`grid items-center gap-3 py-[10px] ${idx < sectors.length - 1 ? 'border-b border-zinc-800/40' : ''}`}
                style={{ gridTemplateColumns: '72px 1fr 56px' }}
              >
                <span className="text-zinc-300 text-[13px] font-semibold text-right truncate">
                  {sector.category}
                </span>
                <div className="relative h-[24px] flex items-center">
                  {/* Center line */}
                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-zinc-700" />

                  {/* Bar — green grows RIGHT from center, red grows LEFT */}
                  {sector.avgChange >= 0 ? (
                    <div
                      className="absolute h-[18px] rounded-r-sm"
                      style={{
                        left: '50%',
                        width: `${barPct}%`,
                        background: 'linear-gradient(to right, rgba(34,197,94,0.15), rgba(34,197,94,0.45))',
                        borderRight: '2px solid #22c55e',
                      }}
                    />
                  ) : (
                    <div
                      className="absolute h-[18px] rounded-l-sm"
                      style={{
                        right: '50%',
                        width: `${barPct}%`,
                        background: 'linear-gradient(to left, rgba(239,68,68,0.15), rgba(239,68,68,0.45))',
                        borderLeft: '2px solid #ef4444',
                      }}
                    />
                  )}
                </div>
                <span
                  className={`text-[13px] font-bold tabular-nums text-right ${
                    sector.avgChange >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}
                >
                  {sector.avgChange >= 0 ? '+' : ''}{sector.avgChange.toFixed(2)}%
                </span>
              </div>
            )
          })}
        </div>

        {/* Axis labels */}
        <div
          className="grid items-center gap-3 mt-2 pt-2 border-t border-zinc-800/40"
          style={{ gridTemplateColumns: '72px 1fr 56px' }}
        >
          <span />
          <div className="flex justify-between relative">
          <span className="text-red-400 text-[8px] uppercase tracking-widest">Losing</span>
          <span className="text-zinc-400 text-[8px] absolute left-1/2 -translate-x-1/2">0%</span>
          <span className="text-green-400 text-[8px] uppercase tracking-widest">Gaining</span>

          </div>
          <span />
        </div>
      </div>

      {/* RIGHT: Commentary sidebar — desktop */}
      <div className="hidden lg:flex flex-col border border-zinc-800 px-5 py-5 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className={`w-2 h-2 rounded-full ${signalColor}`} />
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              Market signal
            </span>
          </div>
          <p className="text-zinc-200 text-[13px] font-semibold leading-snug mb-1">
            {marketSignal}
          </p>
          <p className="text-zinc-400 text-[11px] leading-relaxed">
            {signalDesc}
          </p>
        </div>

        <div className="border-t border-zinc-800/60 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-green-500 text-[8px]">▲</span>
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              Strongest
            </span>
          </div>
          <p className="text-zinc-300 text-[12px] leading-relaxed">
            <span className="text-green-500 font-bold">{bestSector.category}</span>
            {' — '}{bestSector.up} of {bestSector.total} coins up. {strongestDesc}
          </p>
        </div>

        <div className="border-t border-zinc-800/60 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-red-500 text-[8px]">▼</span>
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              Weakest
            </span>
          </div>
          <p className="text-zinc-300 text-[12px] leading-relaxed">
            <span className="text-red-500 font-bold">{worstSector.category}</span>
            {' — '}{worstSector.down} of {worstSector.total} coins down. {weakestDesc}
          </p>
        </div>

        <div className="border-t border-zinc-800/60 pt-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-zinc-400 text-[8px]">⇄</span>
            <span
              className="text-zinc-400 text-[9px] uppercase tracking-widest"
              style={{ fontFamily: "var(--font-saira), 'Saira', sans-serif" }}
            >
              Rotation
            </span>
          </div>
          <p className="text-zinc-400 text-[11px] leading-relaxed">
            {rotationDesc}
          </p>
        </div>
      </div>

      {/* MOBILE: Collapsible commentary */}
      <div className="lg:hidden border border-zinc-800">
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-3 flex items-center justify-between"
        >
          <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${signalColor}`} />
            <span className="text-zinc-200 text-[12px] font-semibold">{marketSignal}</span>
          </div>
          <svg
            width="12" height="12" viewBox="0 0 12 12" fill="none"
            className={`text-zinc-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
          >
            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        {expanded && (
          <div className="px-4 pb-4 space-y-3 border-t border-zinc-800">
            <p className="text-zinc-400 text-[11px] leading-relaxed pt-3">
              {signalDesc}
            </p>
            <div className="flex gap-4">
              <div className="flex-1">
                <span className="text-green-500 text-[9px] uppercase tracking-widest block mb-1">Strongest</span>
                <p className="text-zinc-300 text-[11px]">
                  <span className="text-green-500 font-bold">{bestSector.category}</span>
                  {' — '}{bestSector.up}/{bestSector.total} up
                </p>
              </div>
              <div className="flex-1">
                <span className="text-red-500 text-[9px] uppercase tracking-widest block mb-1">Weakest</span>
                <p className="text-zinc-300 text-[11px]">
                  <span className="text-red-500 font-bold">{worstSector.category}</span>
                  {' — '}{worstSector.down}/{worstSector.total} down
                </p>
              </div>
            </div>
            <p className="text-zinc-400 text-[10px] leading-relaxed">
              {rotationDesc}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}